import React, { useEffect, useState } from "react";
import { Heart, Loader2, Send, Users } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../../lib/auth";

export default function Matches() {
  const [state, setState] = useState({ loading: true, error: null, matches: [], intakeId: null });
  const [existing, setExisting] = useState([]);
  const [sendingId, setSendingId] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [message, setMessage] = useState("");

  const refresh = async () => {
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const [{ data }, { data: conns }] = await Promise.all([
        api.get("/buddy/matches?limit=10"),
        api.get("/buddy/connections"),
      ]);
      setState({ loading: false, error: null, matches: data.matches || [], intakeId: data.intake_id });
      setExisting(conns.items || []);
    } catch (err) {
      const detail = formatApiErrorDetail(err?.response?.data?.detail) || "Could not load matches.";
      setState({ loading: false, error: detail, matches: [], intakeId: null });
    }
  };

  useEffect(() => { refresh(); }, []);

  const sendRequest = async (buddyId) => {
    setSendingId(buddyId);
    try {
      await api.post("/buddy/connections", { buddy_id: buddyId, message: message.trim() || undefined });
      toast.success("Request sent. Your Buddy will respond soon.");
      setOpenId(null);
      setMessage("");
      refresh();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Failed to send request.");
    } finally {
      setSendingId(null);
    }
  };

  if (state.loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-6 h-6 text-[#23803F] animate-spin" />
      </div>
    );
  }

  if (state.error) {
    return (
      <div data-testid="matches-error" className="max-w-2xl mx-auto bg-white rounded-2xl p-8 border border-gray-100 text-center">
        <div className="w-12 h-12 rounded-full bg-[#1A5FAB]/10 mx-auto flex items-center justify-center mb-4">
          <Heart className="w-6 h-6 text-[#1A5FAB]" />
        </div>
        <h1 className="text-xl font-bold text-[#0B132B]">Not ready to match yet</h1>
        <p className="text-[#4B5563] mt-2">{state.error}</p>
      </div>
    );
  }

  const outgoing = existing.filter((c) => c.viewer_role === "member");

  return (
    <div data-testid="matches-page">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-11 h-11 rounded-full bg-[#2D9B4E]/10 flex items-center justify-center">
          <Users className="w-5 h-5 text-[#23803F]" />
        </div>
        <div>
          <h1 className="text-2xl font-extrabold text-[#0B132B]">Your Buddy matches</h1>
          <p className="text-sm text-[#4B5563]">Explainable, deterministic matches — no AI guesswork.</p>
        </div>
      </div>

      {outgoing.length > 0 && (
        <div className="bg-white rounded-2xl p-5 border border-gray-100 mb-6" data-testid="matches-outgoing">
          <div className="text-xs font-bold uppercase tracking-wider text-[#4B5563] mb-3">Your active requests</div>
          <div className="space-y-2">
            {outgoing.map((c) => (
              <div key={c.id} className="flex items-center gap-3 text-sm">
                <span className="font-semibold text-[#0B132B]">{c.buddy_name || "Buddy"}</span>
                <span className="text-xs px-2 py-0.5 rounded-full bg-[#F8F9FB] text-[#4B5563] uppercase tracking-wider">{c.status}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {state.matches.length === 0 ? (
        <div className="bg-white rounded-2xl p-8 border border-gray-100 text-center">
          <div className="text-[#4B5563]">No matches available right now — check back soon.</div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {state.matches.map((m) => (
            <div key={m.buddy.id} data-testid={`match-card-${m.buddy.id}`} className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm hover:shadow-md transition">
              <div className="flex items-start gap-3">
                <div className="w-11 h-11 rounded-full bg-[#1A5FAB] text-white flex items-center justify-center font-bold">
                  {(m.buddy.display_name || "B")[0].toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-[#0B132B]">{m.buddy.display_name}</div>
                  <div className="text-xs text-[#4B5563] mt-0.5">
                    {m.buddy.journey_type} · {m.buddy.location}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-2xl font-extrabold text-[#23803F]">{m.score}</div>
                  <div className="text-[10px] uppercase tracking-wider text-[#4B5563]">score</div>
                </div>
              </div>

              <p className="text-sm text-[#4B5563] mt-3 line-clamp-3">{m.buddy.bio}</p>

              <div className="mt-4 flex flex-wrap gap-1.5" data-testid={`match-reasons-${m.buddy.id}`}>
                {(m.reasons || []).map((r, i) => (
                  <span key={i} className="text-xs px-2 py-1 rounded-full bg-[#2D9B4E]/10 text-[#23803F]">{r}</span>
                ))}
              </div>

              {openId === m.buddy.id ? (
                <div className="mt-4 space-y-2">
                  <textarea
                    data-testid={`match-msg-${m.buddy.id}`}
                    rows={3}
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder="Say hi — a short note about what you're hoping for (optional)."
                    className="w-full px-3 py-2 rounded-xl bg-[#F8F9FB] border border-gray-200 focus:bg-white focus:border-[#2D9B4E] focus:outline-none text-sm"
                  />
                  <div className="flex justify-end gap-2">
                    <button onClick={() => { setOpenId(null); setMessage(""); }} className="px-3 py-2 rounded-full text-sm text-[#4B5563]">Cancel</button>
                    <button
                      data-testid={`match-send-${m.buddy.id}`}
                      disabled={sendingId === m.buddy.id}
                      onClick={() => sendRequest(m.buddy.id)}
                      className="btc-btn-primary text-sm"
                    >
                      {sendingId === m.buddy.id ? "Sending..." : (<><Send className="w-3.5 h-3.5" /> Send request</>)}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="mt-4 flex justify-end">
                  <button
                    data-testid={`match-connect-${m.buddy.id}`}
                    onClick={() => setOpenId(m.buddy.id)}
                    className="btc-btn-primary text-sm"
                  >
                    Ask to connect <Send className="w-3.5 h-3.5" />
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
